import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Search, Plus, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Clock, List, LayoutList, Package, Truck, DollarSign, FileText, Building2, ShoppingCart } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, resolveLiveVatRate, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { mapAchatsStatCards, buildAchatsImportExportProps, buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, addLeadTimeDaysToDate, ensureAchatsDatePickerLocales, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig, fetchAchatsSuppliers } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { getDefaultApprovalStatus } from './achatsConfigDefaults';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

ensureAchatsDatePickerLocales();

const CommandesFournisseursPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.commandesFournisseursPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const dateLocale = getBrandDatePickerLocale();
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(getBrandLocale(i18n.language)) : '-';
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);
  const { settings } = useSettings();
  useArticlesConfig();
  const vatRate = useMemo(
    () => resolveLiveVatRate(settings, achatsConfig),
    [settings?.taxes?.tva, achatsConfig.defaultVatRate, settings],
  );

  const getStatusLabel = (status) => {
    const map = {
      pending: p('status_pending'),
      confirmed: p('status_confirmed'),
      in_transit: p('status_in_transit'),
      received: p('status_received'),
      cancelled: p('status_cancelled'),
    };
    return map[status] || status || p('em_dash');
  };

  const statusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'confirmed', label: p('status_confirmed'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'in_transit', label: p('status_in_transit'), icon: Truck, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'received', label: p('status_received'), icon: Package, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [p]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [formTab, setFormTab] = useState('order');
  const formTabs = useMemo(() => ([
    { id: 'order', label: p('section_order_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'payment', label: p('section_payment_notes'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    order_number: '',
    supplier_id: '',
    supplier_name: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    status: 'pending',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    shipping_cost: '',
    payment_terms: '',
    notes: ''
  });
  
  // Products & Suppliers state
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_orders');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadOrders();
    loadProducts();
    loadSuppliers();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOrders = async () => {
    try {
      const response = await localApiAdapter.get('/achats/purchase-orders');
      setOrders(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      setOrders([]);
    }
  };
  
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      setAvailableProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };
  
  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData({
        ...formData,
        supplier_id: '',
        supplier_name: ''
      });
      return;
    }
    
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || ''
      });
    }
  };
  
  const addOrderItem = () => {
    setOrderItems([...orderItems, {
      id: Date.now(),
      product_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0,
      line_total: 0
    }]);
  };
  
  const removeOrderItem = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };
  
  const updateOrderItem = (id, field, value) => {
    setOrderItems(orderItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        if (field === 'product_id') {
          const product = availableProducts.find(p => p.id.toString() === value.toString());
          if (product) {
            updated.product_name = product.name;
            updated.unit_price = parseFloat(product.price || 0);
          }
        }
        
        updated.line_total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
        
        return updated;
      }
      return item;
    }));
  };
  
  // Auto-calculate totals when items change
  useEffect(() => {
    const subtotal = orderItems.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
    const taxAmount = subtotal * (vatRate / 100);
    const total = subtotal + taxAmount + (parseFloat(formData.shipping_cost) || 0);
    
    setFormData(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      tax_amount: taxAmount.toFixed(2),
      total_amount: total.toFixed(2)
    }));
  }, [orderItems, formData.shipping_cost, vatRate]);

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    in_transit: orders.filter(o => o.status === 'in_transit').length,
    received: orders.filter(o => o.status === 'received').length
  };

  const mainTabs = [
    { id: 'all_orders', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: AlertCircle },
    { id: 'par_fournisseur', name: p('tab_by_supplier'), icon: Building2 },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'confirmed', name: p('filter_confirmed'), icon: CheckCircle },
    { id: 'in_transit', name: p('filter_in_transit'), icon: Truck },
  ];

  const orderDistribution = {
    all_orders: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { filterKey: 'confirmed', name: p('dist_confirmed'), count: stats.confirmed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'in_transit', name: p('dist_in_transit'), count: stats.in_transit, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'received', name: p('dist_received'), count: stats.received, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.pending + stats.confirmed + stats.in_transit, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.received, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_fournisseur: {
      title: p('dist_by_supplier_title'),
      data: (() => {
        const supplierCounts = {};
        orders.forEach(o => {
          const supplier = o.supplier_name || '__undefined__';
          supplierCounts[supplier] = (supplierCounts[supplier] || 0) + 1;
        });
        return Object.entries(supplierCounts)
          .slice(0, 3)
          .map(([name, count], idx) => ({
            filterKey: name,
            name: name === '__undefined__' ? p('supplier_undefined') : name,
            count,
            color: idx === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : idx === 1 ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-gradient-to-r from-pink-500 to-pink-600',
          }));
      })(),
    },
  };

  const distributionInfo = orderDistribution[mainViewMode] || orderDistribution.all_orders;

  const baseFilteredOrders = orders.filter(o => {
    if (dateRange.start || dateRange.end) {
      const orderDate = new Date(o.order_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (orderDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (orderDate > endOfDay) return false;
      }
    }
    
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (searchTerm && !o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !o.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_orders') {
      if (quickFilter === 'pending' && o.status !== 'pending') return false;
      if (quickFilter === 'confirmed' && o.status !== 'confirmed') return false;
      if (quickFilter === 'in_transit' && o.status !== 'in_transit') return false;
    }
    
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_orders':
          if (activeDistributionFilter === 'pending' && o.status !== 'pending') return false;
          if (activeDistributionFilter === 'confirmed' && o.status !== 'confirmed') return false;
          if (activeDistributionFilter === 'in_transit' && o.status !== 'in_transit') return false;
          if (activeDistributionFilter === 'received' && o.status !== 'received') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'in_progress' && !['pending', 'confirmed', 'in_transit'].includes(o.status)) return false;
          if (activeDistributionFilter === 'completed' && o.status !== 'received') return false;
          break;
        case 'par_fournisseur':
          if (activeDistributionFilter === '__undefined__' && o.supplier_name) return false;
          if (activeDistributionFilter !== '__undefined__' && o.supplier_name !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('order_number'),
      textColumnRule('supplier_name'),
      textColumnRule('order_date', (r) => (r.order_date ? fmtDate(r.order_date) : '')),
      textColumnRule('expected_delivery_date', (r) => (r.expected_delivery_date ? fmtDate(r.expected_delivery_date) : '')),
      textColumnRule('total_amount', (r) => String(r.total_amount ?? '')),
      textColumnRule('payment_terms'),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [i18n.language],
  );

  const { filtered: filteredOrders, displayed: displayedOrders } = useAchatsListDisplay(
    baseFilteredOrders,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredOrders);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const generateOrderNumber = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'purchaseOrder', 'CMDF', orders, (o) => o.order_number),
    [achatsSettings, orders],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (orderItems.length === 0) {
      alert(p('alert_add_product'));
      return;
    }
    
    try {
      const orderNumber = resolveAchatsEntityReference(
        achatsSettings,
        'purchaseOrder',
        'CMDF',
        orders,
        (o) => o.order_number,
        formData.order_number,
      );
      const submitData = { 
        order_number: orderNumber,
        supplier_id: parseInt(formData.supplier_id) || null,
        supplier_name: formData.supplier_name,
        order_date: formData.order_date || new Date().toISOString().split('T')[0],
        expected_delivery_date: formData.expected_delivery_date,
        status: formData.status || getDefaultApprovalStatus(achatsSettings, 'purchaseOrder'),
        subtotal: parseFloat(formData.subtotal) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        payment_terms: formData.payment_terms,
        notes: formData.notes,
        items: orderItems.map(item => ({
          product_id: parseInt(item.product_id),
          product_name: item.product_name,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          line_total: parseFloat(item.line_total)
        }))
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/achats/purchase-orders/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/purchase-orders', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error') + ': ' + (error.message || p('alert_unknown_error')));
    } finally {
      resetForm();
      loadOrders();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      order_number: record.order_number || '',
      supplier_id: record.supplier_id?.toString() || '',
      supplier_name: record.supplier_name || '',
      order_date: record.order_date || '',
      expected_delivery_date: record.expected_delivery_date || '',
      status: record.status || 'pending',
      subtotal: record.subtotal?.toString() || '',
      tax_amount: record.tax_amount?.toString() || '',
      total_amount: record.total_amount?.toString() || '',
      shipping_cost: record.shipping_cost?.toString() || '',
      payment_terms: record.payment_terms || '',
      notes: record.notes || ''
    });
    setOrderItems(record.items || []);
    setFormTab('order');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/purchase-orders/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadOrders();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { defaultValue: p('alert_unknown_error') }));
      }
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/purchase-orders/${recordId}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      loadOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      notifyError(showNotify, p('alert_save_error') + ': ' + (error.message || p('alert_unknown_error')));
    }
  };

  const handleConvertToLedger = async (order) => {
    if (!window.confirm(p('convert_confirm', { number: order.order_number }))) return;
    
    try {
      const response = await localApiAdapter.post(`/achats/livre-des-achats/convert/${order.id}`);
      alert(p('convert_success', { number: response.purchase_number }));
      await loadOrders(); // Refresh to update UI
    } catch (error) {
      alert(p('convert_error') + ' ' + (error.response?.data?.error || error.message || p('alert_unknown_error')));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/achats/purchase-orders/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        loadOrders();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { defaultValue: p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      order_number: '',
      supplier_id: '',
      supplier_name: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      status: 'pending',
      subtotal: '',
      tax_amount: '',
      total_amount: '',
      shipping_cost: '',
      payment_terms: '',
      notes: ''
    });
    setOrderItems([]);
    setEditingRecord(null);
    setFormTab('order');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'order_number', label: p('col_order_number') },
      { key: 'supplier_name', label: p('col_supplier') },
      { key: 'order_date', label: p('export_col_order_date') },
      { key: 'expected_delivery_date', label: p('export_col_delivery_date') },
      { key: 'total_amount', label: p('export_col_total_amount') },
      { key: 'status', label: p('col_status') },
      { key: 'payment_terms', label: p('col_payment_terms') },
      { key: 'notes', label: p('label_notes') }
    ];

    const dataToExport = filteredOrders;
    const fileName = `commandes_fournisseurs_${new Date().toISOString().split('T')[0]}`;

    try {
      exportToCSV(dataToExport, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredOrders;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_order_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('label_date')}</th>
                <th>${p('col_amount')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.order_number || 'N/A'}</td>
                  <td>${item.supplier_name || 'N/A'}</td>
                  <td>${fmtDate(item.order_date)}</td>
                  <td>${formatBrandMoney((item.total_amount || 0))}</td>
                  <td>${item.status || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const filteredData = filteredOrders;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_order_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('export_col_order_date')}</th>
                <th>${p('col_delivery_date')}</th>
                <th>${p('export_col_total_amount')}</th>
                <th>${p('col_status')}</th>
                <th>${p('col_payment_terms')}</th>
                <th>${p('label_notes')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.order_number || ''}</td>
                  <td>${item.supplier_name || ''}</td>
                  <td>${fmtDate(item.order_date)}</td>
                  <td>${item.expected_delivery_date ? fmtDate(item.expected_delivery_date) : ''}</td>
                  <td>${formatBrandMoney(item.total_amount || 0)}</td>
                  <td>${item.status || ''}</td>
                  <td>${item.payment_terms || ''}</td>
                  <td>${item.notes || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportExcel('commandes_fournisseurs', p('title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'order_number', label: p('col_order_number') },
      { key: 'supplier_name', label: p('col_supplier') },
      { key: 'order_date', label: p('export_col_order_date') },
      { key: 'total_amount', label: p('export_col_total_amount') }
    ];

    const schema = {
      requiredFields: ['supplier_name', 'order_date'],
      validators: {
        total_amount: (value) => !value || !isNaN(parseFloat(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/purchase-orders', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          
          await loadOrders();
          
          const message = p('import_success', { total: result.totalCount, imported: successCount }) +
            (result.invalidCount > 0 ? '\n' + p('import_errors_suffix', { count: result.invalidCount }) + '\n\n' + result.errors.slice(0, 5).join('\n') : '');
          
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredOrders.map((r) => r.id));
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{getStatusLabel(status)}</span>;
  };


  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total_orders'), value: stats.total, icon: ClipboardList, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600', variant: 'forest' },
      { key: 'confirmed', label: p('stat_confirmed'), value: stats.confirmed, icon: CheckCircle, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'in_transit', label: p('stat_in_transit'), value: stats.in_transit, icon: Truck, color: 'from-purple-500 to-purple-600', variant: 'forest' },
    ],
    [stats, p],
  );

  const commandesColumnKeys = useMemo(
    () => ['order_number', 'supplier_name', 'order_date', 'expected_delivery_date', 'total_amount', 'payment_terms', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(commandesColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'order_number', labelKey: 'col_order_number' },
      { key: 'supplier_name', labelKey: 'col_supplier' },
      { key: 'order_date', labelKey: 'col_order_date' },
      { key: 'expected_delivery_date', labelKey: 'col_delivery_date' },
      { key: 'total_amount', labelKey: 'col_amount' },
      { key: 'payment_terms', labelKey: 'col_payment' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const advancedOptionsContent = (
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_orders' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_by_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enablePurchaseOrders ? p('notice_orders_disabled', { defaultValue: 'أوامر الشراء معطّلة — فعّلها من إعدادات المشتريات ← طلبات وأوامر الشراء.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={ClipboardList}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedRecords.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedRecords.length})`, title: p('bulk_delete_confirm', { count: selectedRecords.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={() => {
          if (!achatsConfig.enablePurchaseOrders) {
            alert(p('feature_disabled', { defaultValue: 'أوامر الشراء معطّلة من إعدادات المشتريات.' }));
            return;
          }
          const orderDate = new Date().toISOString().split('T')[0];
          setEditingRecord(null);
          setFormData({
            order_number: generateOrderNumber(),
            supplier_id: '',
            supplier_name: '',
            order_date: orderDate,
            expected_delivery_date: addLeadTimeDaysToDate(orderDate, achatsConfig.defaultLeadTimeDays),
            status: getDefaultApprovalStatus(achatsSettings, 'purchaseOrder'),
            subtotal: '',
            tax_amount: '',
            total_amount: '',
            shipping_cost: '',
            payment_terms: achatsConfig.defaultPaymentTerms || '',
            notes: '',
          });
          setOrderItems([]);
          setFormTab('order');
          setShowModal(true);
        }}
        createLabel={p('new_order')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadOrders} className="refresh-button" title={t('common.refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
                  tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('order_number') ? <TableColumnHeaderFilter {...colHeader('order_number', p('col_order_number'))} /> : null}
                  {isColumnVisible('supplier_name') ? <TableColumnHeaderFilter {...colHeader('supplier_name', p('col_supplier'))} /> : null}
                  {isColumnVisible('order_date') ? <TableColumnHeaderFilter {...colHeader('order_date', p('col_order_date'))} /> : null}
                  {isColumnVisible('expected_delivery_date') ? <TableColumnHeaderFilter {...colHeader('expected_delivery_date', p('col_delivery_date'))} /> : null}
                  {isColumnVisible('total_amount') ? <TableColumnHeaderFilter {...colHeader('total_amount', p('col_amount'))} /> : null}
                  {isColumnVisible('payment_terms') ? <TableColumnHeaderFilter {...colHeader('payment_terms', p('col_payment'))} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedOrders.length > 0 ? (
                  displayedOrders.map((record, index) => {
                    const getOrderDetails = () => {
                      const deliveryInfo = record.expected_delivery_date ? p('delivery_expected', { date: fmtDate(record.expected_delivery_date) }) : p('no_delivery_date');
                      const paymentInfo = record.payment_terms || p('terms_undefined');
                      return `${deliveryInfo} | ${paymentInfo}`;
                    };

                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                        
                        {isColumnVisible('order_number') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.order_number}</span>
                            <span className="text-white/50 text-xs mt-0.5">{getOrderDetails()}</span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('supplier_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.supplier_name}</span>
                            <span className="text-white/50 text-xs mt-0.5">ID: {record.supplier_id || 'N/A'}</span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('order_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {fmtDate(record.order_date)}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.order_date ? p('days_ago', { count: Math.floor((Date.now() - new Date(record.order_date).getTime()) / (1000 * 60 * 60 * 24)) }) : ''}
                            </span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('expected_delivery_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.expected_delivery_date ? fmtDate(record.expected_delivery_date) : '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.expected_delivery_date ? (new Date(record.expected_delivery_date) > new Date() ? p('upcoming') : p('overdue')) : p('undefined_delivery')}
                            </span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('total_amount') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney((record.total_amount || 0))}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('subtotal_label')} {formatBrandMoney(record.subtotal || 0)} | {p('vat_label')} {formatBrandMoney(record.tax_amount || 0)}
                            </span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('payment_terms') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.payment_terms || '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('shipping_cost_label')} {formatBrandMoney((record.shipping_cost || 0))}</span>
                          </div>
                        </td>
                        ) : null}
                        
                        {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                        ) : null}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <QawaemErpRowActions
                              onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                              onEdit={() => handleEdit(record)}
                              onDelete={() => handleDelete(record.id)}
                              viewTitle={p('action_view')}
                              editTitle={p('action_edit')}
                              deleteTitle={p('action_delete')}
                            />
                            {record.status === 'received' && (
                              <button onClick={() => handleConvertToLedger(record)} className="p-1.5 text-purple-400 hover:text-purple-300 transition-colors" title={p('convert_to_purchase')}><DollarSign size={16} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ClipboardList size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedOrders.length > 0 ? (
              displayedOrders.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ClipboardList size={20} className="text-emerald-400" />
                          {record.order_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                        {record.order_date && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {fmtDate(record.order_date)}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_order_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_order_number')}:</span> {record.order_number}</div>
                            <div><span className="font-medium">{p('label_date')}:</span> {fmtDate(record.order_date)}</div>
                            <div><span className="font-medium">{p('label_expected_delivery')}:</span> {record.expected_delivery_date ? fmtDate(record.expected_delivery_date) : '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}:</span> {record.supplier_name}</div>
                            <div><span className="font-medium">{p('label_id')}:</span> {record.supplier_id || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_subtotal')}:</span> {formatBrandMoney((record.subtotal || 0))}</div>
                            <div><span className="font-medium">{p('label_vat')}:</span> {formatBrandMoney((record.tax_amount || 0))}</div>
                            <div><span className="font-medium">{p('label_total')}:</span> <span className="text-white font-bold">{formatBrandMoney((record.total_amount || 0))}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_payment_delivery')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('payment_conditions')}</span> {record.payment_terms || '-'}</div>
                            <div><span className="font-medium">{p('label_shipping')}:</span> {formatBrandMoney((record.shipping_cost || 0))}</div>
                            {record.notes && (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('label_notes')}:</span>
                                <p className="text-white/60 text-xs mt-1">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 ml-4">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={p('action_view')}
                        editTitle={p('action_edit')}
                        deleteTitle={p('action_delete')}
                      />
                      {record.status === 'received' && (
                        <button onClick={() => handleConvertToLedger(record)} className="p-1.5 text-purple-400 hover:text-purple-300 transition-colors" title={p('convert_to_purchase')}>
                          <DollarSign className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <ClipboardList size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>
                    <ClipboardList size={18} className="text-emerald-400" />
                    {p('section_order_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_order_number')}</p>
                      <p className={m.viewValueLg}>{viewingRecord.order_number}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_order_date')}</p>
                      <p className={m.viewValue}>{fmtDate(viewingRecord.order_date)}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_expected_delivery')}</p>
                      <p className={m.viewValue}>{viewingRecord.expected_delivery_date ? fmtDate(viewingRecord.expected_delivery_date) : '-'}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_status')}</p>
                      <div className="flex">{getStatusBadge(viewingRecord.status)}</div>
                    </div>
                  </div>
                </div>

                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>
                    <Building2 size={18} className="text-blue-400" />
                    {p('section_supplier_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValue}>{viewingRecord.supplier_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_supplier_id')}</p>
                      <p className={m.viewValue}>{viewingRecord.supplier_id || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <DollarSign size={18} className="text-green-400" />
                      {p('card_amounts')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('label_subtotal')}</p>
                        <p className={m.viewValue}>{formatBrandMoney((viewingRecord.subtotal || 0))}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('label_vat')}</p>
                        <p className={m.viewValue}>{formatBrandMoney((viewingRecord.tax_amount || 0))}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('label_shipping')}</p>
                        <p className={m.viewValue}>{formatBrandMoney((viewingRecord.shipping_cost || 0))}</p>
                      </div>
                      <div className="pt-2 border-t border-white/30">
                        <p className={m.viewLabel}>{p('export_col_total_amount')}</p>
                        <p className="text-white font-bold text-xl">{formatBrandMoney((viewingRecord.total_amount || 0))}</p>
                      </div>
                    </div>
                  </div>

                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <FileText size={18} className="text-yellow-400" />
                      {p('section_extra_details')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('col_payment_terms')}</p>
                        <p className={m.viewValue}>{viewingRecord.payment_terms || p('supplier_undefined')}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('label_warehouse')}</p>
                        <p className={m.viewValue}>{viewingRecord.warehouse_id || p('supplier_undefined')}</p>
                      </div>
                      {viewingRecord.notes && (
                        <div>
                          <p className={m.viewLabel}>{p('label_notes')}</p>
                          <p className="text-white/80 text-sm bg-white/5 p-3 rounded-lg">{viewingRecord.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
        </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={ShoppingCart}
        formId="order-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="order-form" onSubmit={handleSubmit}>
                {formTab === 'order' && (
                  <div className="space-y-4">
                    <div className={ACHATS_FORM_GRID}>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('label_order_number')}</label>
                        <input
                          type="text"
                          value={formData.order_number}
                          onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                          className={`${cfgTheme.input} font-mono`}
                          placeholder={p('placeholder_auto_generated', { defaultValue: 'يُولَّد تلقائياً' })}
                        />
                      </div>
                    </div>
                    <div className={ACHATS_FORM_GRID}>
                      <div>
                        <label className={cfgTheme.label}><RequiredLabelText text={p('field_order_date')} required /></label>
                        <input type="date" required value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_expected_delivery')}</label>
                        <input type="date" value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}><RequiredLabelText text={p('label_supplier')} required /></label>
                        {loadingSuppliers ? (
                          <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                        ) : (
                          <CustomSelect
                            value={formData.supplier_id}
                            onChange={(value) => handleSupplierSelection(value)}
                            options={availableSuppliers.map(supplier => ({
                              value: supplier.id,
                              label: `${supplier.company_name || supplier.name || p('supplier_unnamed')} - ${supplier.contact_person || supplier.phone1 || ''}`
                            }))}
                            placeholder={p('placeholder_select_supplier')}
                            searchable
                          />
                        )}
                      </div>
                      <div>
                        <label className={cfgTheme.label}><RequiredLabelText text={p('col_status')} required /></label>
                        <div className="relative" ref={statusDropdownRef}>
                          <button
                            type="button"
                            data-status-dropdown-button="true"
                            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"
                          >
                            <span>{statusOptions.find(s => s.value === formData.status)?.label || p('select_placeholder')}</span>
                            <ChevronDown size={16} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showStatusDropdown && ReactDOM.createPortal(
                            <div
                              data-status-dropdown="true"
                              className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden"
                              style={{
                                backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                                position: 'fixed',
                                ...(statusDropdownRef.current && (() => {
                                  const rect = statusDropdownRef.current.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const spaceAbove = rect.top;
                                  const dropdownHeight = 220;
                                  if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                                    return { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width };
                                  }
                                  return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                })()),
                                zIndex: 99999,
                                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                              }}
                            >
                              {statusOptions.map(option => (
                                <button key={option.value} type="button" onClick={() => { setFormData({ ...formData, status: option.value }); setShowStatusDropdown(false); }} className="w-full flex items-center px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left text-white text-sm font-medium">
                                  <option.icon size={16} className="mr-2" />
                                  {option.label}
                                </button>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={addOrderItem}
                      className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-200 px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium"
                    >
                      <Plus size={18} />
                      {p('add_product')}
                    </button>
                    {orderItems.length > 0 && (
                      <div className="glass-table-scroll overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="px-2 py-2 text-left text-white/70 text-xs font-medium">#</th>
                              <th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('field_product_required')}</th>
                              <th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_qty')}</th>
                              <th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_unit_price_short')}</th>
                              <th className="px-2 py-2 text-left text-white/70 text-xs font-medium">{p('col_line_total')}</th>
                              <th className="px-2 py-2 text-center text-white/70 text-xs font-medium">{p('col_action')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderItems.map((item, index) => (
                              <tr key={item.id} className="border-b border-white/5">
                                <td className="px-2 py-2 text-white/60 text-sm">{index + 1}</td>
                                <td className="py-2 px-2">
                                  <CustomSelect
                                    value={item.product_id}
                                    onChange={(value) => {
                                      const product = availableProducts.find(p => p.id === value);
                                      updateOrderItem(item.id, 'product_id', value);
                                      if (product) {
                                        updateOrderItem(item.id, 'product_name', product.name);
                                        updateOrderItem(item.id, 'unit_price', product.price || 0);
                                      }
                                    }}
                                    options={availableProducts.map(product => ({
                                      value: product.id,
                                      label: `${product.name} - ${formatBrandMoney(product.price || 0)}`
                                    }))}
                                    placeholder={p('placeholder_select_product')}
                                    searchable
                                    className="min-w-[200px]"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => updateOrderItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                    className="w-20 bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.unit_price}
                                    disabled
                                    className="w-28 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white/50 text-sm cursor-not-allowed"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={(item.quantity * item.unit_price).toFixed(2)}
                                    disabled
                                    className="w-28 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white/50 text-sm cursor-not-allowed"
                                  />
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => removeOrderItem(item.id)}
                                    className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {formTab === 'payment' && (
                  <div className="space-y-4">
                    <div className={ACHATS_FORM_GRID}>
                      <div>
                        <label className={cfgTheme.label}>{p('label_shipping')}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.shipping_cost || ''}
                          onChange={(e) => setFormData({ ...formData, shipping_cost: parseFloat(e.target.value) || 0 })}
                          className={cfgTheme.input}
                          placeholder={p('shipping_placeholder')}
                        />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('label_subtotal')}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.subtotal || 0}
                          disabled
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/50 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('vat_rate_label', { rate: vatRate })}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.tax_amount || 0}
                          disabled
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/50 cursor-not-allowed"
                        />
                      </div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold text-lg">{p('export_col_total_amount')}</span>
                        <span className="text-blue-400 font-bold text-2xl">{formatBrandMoney(formData.total_amount || 0)}</span>
                      </div>
                    </div>
                    <div className={ACHATS_FORM_GRID}>
                      <div>
                        <label className={cfgTheme.label}>{p('label_payment_terms')}</label>
                        <input type="text" value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} className={cfgTheme.input} placeholder={p('payment_terms_placeholder')} />
                      </div>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('label_notes')}</label>
                        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" rows="4" placeholder={p('order_notes_placeholder')} />
                      </div>
                    </div>
                  </div>
                )}

        </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default CommandesFournisseursPage;


